import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LogOut, LayoutGrid, Trash2 } from 'lucide-react';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { HabitProvider } from '../../context/HabitContext';
import { SelectedDateProvider } from '../../context/SelectedDateContext';
import AppNav from './AppNav';
import YearlyTracker from '../YearlyTracker/YearlyTracker';
import DailyTracker from '../DailyTracker/DailyTracker';
import LoginScreen from '../LoginScreen/LoginScreen';
import Onboarding from '../Onboarding/Onboarding';
import PendingDeletion from '../PendingDeletion/PendingDeletion';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { useWheelLife } from '../../hooks/useWheelLife';
import DailyLayoutSettings from '../DailyTracker/DailyLayoutSettings';
import { DEFAULT_DAILY_SECTIONS, DEFAULT_HIDDEN_SECTIONS } from '../../types/onboarding';
import { locales, Locale } from '../../i18n';
import { clearSignInLocale, getLocaleSyncAction, peekSignInLocale } from '../../utils/localePreference';
import Spinner from '../common/Spinner';

type ThemeMode = 'dark' | 'light';

interface AppShellProps {
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
}

interface AuthenticatedShellProps extends AppShellProps {
  theme: ThemeMode;
  bgPrimaryOverrides: Partial<Record<ThemeMode, string>>;
  settingsOpen: boolean;
  setSettingsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  settingsRef: React.RefObject<HTMLDivElement>;
  signOut: () => Promise<void>;
  t: ReturnType<typeof useIntl>['formatMessage'];
  onThemeChange: (next: ThemeMode) => void;
  currentBgPrimary: string;
  handleBgPrimaryChange: (value: string) => void;
  handleBgPrimaryReset: () => void;
}

const BG_PRIMARY_STORAGE_KEY = 'theme-bg-primary-overrides';
const ONBOARDING_FORCE_STORAGE_KEY = 'web-onboarding-force';
const THEME_BG_PRIMARY_DEFAULTS: Record<ThemeMode, string> = {
  dark: '#1c1b16',
  light: '#F8F7F8',
};

function readBgPrimaryOverrides(): Partial<Record<ThemeMode, string>> {
  try {
    const raw = localStorage.getItem(BG_PRIMARY_STORAGE_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw) as Partial<Record<ThemeMode, string>>;
    const next: Partial<Record<ThemeMode, string>> = {};

    (['dark', 'light'] as ThemeMode[]).forEach(theme => {
      const value = parsed[theme];
      if (typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value)) {
        next[theme] = value;
      }
    });

    return next;
  } catch {
    return {};
  }
}

function readStoredForceOnboarding(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_FORCE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}


const AppShell: React.FC<AppShellProps> = ({ locale, onLocaleChange }) => {
  const { formatMessage: t } = useIntl();
  const { session, loading, signOut, deletionDueAt } = useAuth();
  const [theme, setTheme] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem('theme');
    return stored === 'dark' || stored === 'light' ? stored : 'light';
  });
  const [bgPrimaryOverrides, setBgPrimaryOverrides] = useState<Partial<Record<ThemeMode, string>>>(() => readBgPrimaryOverrides());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.setAttribute('data-theme', 'dark');
    } else {
      root.removeAttribute('data-theme');
    }

    const bgPrimaryOverride = bgPrimaryOverrides[theme];
    if (bgPrimaryOverride) {
      root.style.setProperty('--bg-primary', bgPrimaryOverride);
      root.setAttribute('data-bg-custom', '');
    } else {
      root.style.removeProperty('--bg-primary');
      root.removeAttribute('data-bg-custom');
    }

    localStorage.setItem('theme', theme);
  }, [theme, bgPrimaryOverrides]);

  useEffect(() => {
    localStorage.setItem(BG_PRIMARY_STORAGE_KEY, JSON.stringify(bgPrimaryOverrides));
  }, [bgPrimaryOverrides]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setSettingsOpen(false);
      }
    };

    if (settingsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [settingsOpen]);

  const currentBgPrimary = bgPrimaryOverrides[theme] ?? THEME_BG_PRIMARY_DEFAULTS[theme];

  const handleBgPrimaryChange = (value: string) => {
    setBgPrimaryOverrides(prev => ({
      ...prev,
      [theme]: value,
    }));
  };

  const handleBgPrimaryReset = () => {
    setBgPrimaryOverrides(prev => {
      const next = { ...prev };
      delete next[theme];
      return next;
    });
  };

  if (loading) {
    return (
      <div className="app-shell-loader">
        <Spinner size="lg" block />
      </div>
    );
  }
  if (!session) return <LoginScreen locale={locale} onLocaleChange={onLocaleChange} />;
  if (deletionDueAt) return <PendingDeletion />;

  return (
    <AuthenticatedShell
      locale={locale}
      onLocaleChange={onLocaleChange}
      theme={theme}
      bgPrimaryOverrides={bgPrimaryOverrides}
      settingsOpen={settingsOpen}
      setSettingsOpen={setSettingsOpen}
      settingsRef={settingsRef}
      signOut={signOut}
      t={t}
      onThemeChange={setTheme}
      currentBgPrimary={currentBgPrimary}
      handleBgPrimaryChange={handleBgPrimaryChange}
      handleBgPrimaryReset={handleBgPrimaryReset}
    />
  );
};

function AuthenticatedShell({
  locale,
  onLocaleChange,
  theme,
  bgPrimaryOverrides,
  settingsOpen,
  setSettingsOpen,
  settingsRef,
  signOut,
  t,
  onThemeChange,
  currentBgPrimary,
  handleBgPrimaryChange,
  handleBgPrimaryReset,
}: AuthenticatedShellProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { deleteAccount } = useAuth();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(false);
  const { preferences, settings, isLoading: prefsLoading, isUpdating, updatePreferences, updateSettings } = useUserPreferences();
  const [forceOnboarding, setForceOnboarding] = useState<boolean>(() => readStoredForceOnboarding());
  const [completionOverride, setCompletionOverride] = useState<string | null | undefined>(undefined);
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [hexDraft, setHexDraft] = useState(currentBgPrimary.replace(/^#/, '').toUpperCase());
  const localeSyncedRef = useRef(false);
  const onboardingComplete = completionOverride ?? preferences?.onboarding_completed_at ?? null;
  const { sections } = useWheelLife({ enabled: !prefsLoading && !onboardingComplete });

  useEffect(() => {
    setHexDraft(currentBgPrimary.replace(/^#/, '').toUpperCase());
  }, [currentBgPrimary]);

  const commitHexDraft = (raw: string) => {
    const trimmed = raw.trim();
    const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    if (/^#[0-9a-f]{6}$/i.test(withHash)) {
      handleBgPrimaryChange(withHash.toLowerCase());
      return;
    }
    setHexDraft(currentBgPrimary.replace(/^#/, '').toUpperCase());
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDeleteError(false);
    try {
      await deleteAccount();
      queryClient.clear();
    } catch {
      setDeleteError(true);
      setDeleting(false);
    }
  };

  const hasExistingData = sections.length > 0 && sections[0]?.id !== 'health';
  const needsOnboarding = !prefsLoading && !onboardingComplete && !hasExistingData;
  const onboardingActive = !prefsLoading && (forceOnboarding || needsOnboarding);

  useEffect(() => {
    setCompletionOverride(undefined);
  }, [preferences?.onboarding_completed_at]);

  useEffect(() => {
    try {
      localStorage.setItem(ONBOARDING_FORCE_STORAGE_KEY, forceOnboarding ? 'true' : 'false');
    } catch {
      // ignore storage errors
    }
  }, [forceOnboarding]);

  const persistLocalePreference = useCallback(async (nextLocale: Locale) => {
    onLocaleChange(nextLocale);
    await updatePreferences({ locale: nextLocale });
  }, [onLocaleChange, updatePreferences]);

  const handleSettingsLocaleChange = useCallback((nextLocale: Locale) => {
    void persistLocalePreference(nextLocale);
  }, [persistLocalePreference]);

  const completeOnboarding = useCallback(async () => {
    const completedAt = new Date().toISOString();
    setCompletionOverride(completedAt);
    setForceOnboarding(false);
    navigate('/');
    await updatePreferences({ onboarding_completed_at: completedAt });
  }, [navigate, updatePreferences]);

  useEffect(() => {
    if (!prefsLoading && !onboardingComplete && hasExistingData && !forceOnboarding) {
      updatePreferences({ onboarding_completed_at: new Date().toISOString(), locale });
    }
  }, [forceOnboarding, hasExistingData, locale, onboardingComplete, prefsLoading, updatePreferences]);

  useEffect(() => {
    if (prefsLoading || isUpdating || localeSyncedRef.current) return;
    const signInLocale = peekSignInLocale();
    const action = getLocaleSyncAction(
      locale,
      preferences?.locale,
      Boolean(onboardingComplete),
      signInLocale,
    );
    if (!action) {
      localeSyncedRef.current = true;
      if (signInLocale) clearSignInLocale();
      return;
    }
    localeSyncedRef.current = true;
    if (action.type === 'apply') {
      onLocaleChange(action.locale);
      if (signInLocale) clearSignInLocale();
      return;
    }
    if (locale !== action.locale) {
      onLocaleChange(action.locale);
    }
    void updatePreferences({ locale: action.locale }).then(() => {
      if (signInLocale) clearSignInLocale();
    });
  }, [isUpdating, locale, onLocaleChange, onboardingComplete, preferences?.locale, prefsLoading, updatePreferences]);

  if (prefsLoading) {
    return (
      <div className="app-shell-loader">
        <Spinner size="lg" block />
      </div>
    );
  }

  return (
        <SelectedDateProvider>
      <HabitProvider>
        <div className={`app-shell${onboardingActive ? ' app-shell--onboarding' : ''}`}>
        <AppNav
          settingsOpen={settingsOpen}
          setSettingsOpen={setSettingsOpen}
          settingsRef={settingsRef}
          settingsPanel={settingsOpen ? (
                <div className="app-nav__settings-dropdown" role="dialog" aria-labelledby="app-settings-title">
                  <div className="app-nav__settings-header">
                    <p className="app-nav__settings-eyebrow">{t({ id: 'settings.title' })}</p>
                    <h2 id="app-settings-title" className="app-nav__settings-title">{t({ id: 'settings.subtitle' })}</h2>
                  </div>

                  <div className="app-nav__settings-item">
                    <div className="app-nav__settings-copy">
                      <span className="app-nav__settings-label">{t({ id: 'settings.themeLabel' })}</span>
                      <span className="app-nav__settings-value">{theme === 'dark' ? t({ id: 'settings.darkMode' }) : t({ id: 'settings.lightMode' })}</span>
                    </div>
                    <div className="app-nav__theme-seg" role="group" aria-label={t({ id: 'settings.themeLabel' })}>
                      <button
                        type="button"
                        className={theme === 'light' ? 'is-active' : undefined}
                        aria-pressed={theme === 'light'}
                        onClick={() => onThemeChange('light')}
                      >
                        {t({ id: 'settings.themeLight' })}
                      </button>
                      <button
                        type="button"
                        className={theme === 'dark' ? 'is-active' : undefined}
                        aria-pressed={theme === 'dark'}
                        onClick={() => onThemeChange('dark')}
                      >
                        {t({ id: 'settings.themeDark' })}
                      </button>
                    </div>
                  </div>

                  <div className="app-nav__settings-item app-nav__settings-item--stacked">
                    <div className="app-nav__settings-copy">
                      <span className="app-nav__settings-label">{t({ id: 'settings.bgLabel' })}</span>
                      <span className="app-nav__settings-value">{t({ id: 'settings.bgHint' })}</span>
                    </div>

                    <div className="app-nav__theme-color-row">
                      <label className="app-nav__theme-color-wheel" aria-label={t({ id: 'settings.bgLabel' })}>
                        <input
                          className="app-nav__theme-color-input"
                          type="color"
                          value={currentBgPrimary}
                          onChange={event => handleBgPrimaryChange(event.target.value)}
                        />
                      </label>

                      <div className="app-nav__theme-color-meta">
                        <input
                          className="app-nav__theme-color-value"
                          value={hexDraft}
                          spellCheck={false}
                          aria-label={t({ id: 'settings.bgLabel' })}
                          onChange={event => setHexDraft(event.target.value)}
                          onBlur={() => commitHexDraft(hexDraft)}
                          onKeyDown={event => {
                            if (event.key === 'Enter') {
                              event.currentTarget.blur();
                            }
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        className="app-nav__theme-color-reset"
                        onClick={handleBgPrimaryReset}
                        disabled={!bgPrimaryOverrides[theme]}
                      >
                        {t({ id: 'settings.reset' })}
                      </button>
                    </div>
                  </div>

                  <div className="app-nav__settings-item">
                    <div className="app-nav__settings-copy">
                      <span className="app-nav__settings-label">{t({ id: 'settings.layoutLabel' })}</span>
                      <span className="app-nav__settings-value">{t({ id: 'settings.layoutHint' })}</span>
                    </div>
                    <button
                      type="button"
                      className="app-nav__settings-icon-btn"
                      onClick={() => {
                        setSettingsOpen(false);
                        setLayoutOpen(true);
                      }}
                      title={t({ id: 'settings.layoutChange' })}
                      aria-label={t({ id: 'settings.layoutChange' })}
                    >
                      <LayoutGrid size={15} />
                    </button>
                  </div>

                  <div className="app-nav__settings-item app-nav__settings-item--stacked">
                    <div className="app-nav__settings-copy">
                      <span className="app-nav__settings-label">{t({ id: 'settings.langLabel' })}</span>
                      <span className="app-nav__settings-value">{t({ id: 'settings.langHint' })}</span>
                    </div>
                    <select
                      className="app-nav__locale-select"
                      value={locale}
                      onChange={event => handleSettingsLocaleChange(event.target.value as Locale)}
                      aria-label={t({ id: 'settings.langLabel' })}
                    >
                      {(Object.keys(locales) as Locale[]).map(nextLocale => (
                        <option key={nextLocale} value={nextLocale}>
                          {nextLocale === 'en' ? 'English' : 'Українська'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="app-nav__settings-footer">
                    <button
                      type="button"
                      onClick={signOut}
                      className="app-nav__settings-logout"
                    >
                      <LogOut size={16} />
                      <span>{t({ id: 'auth.signOut' })}</span>
                    </button>

                    {confirmingDelete ? (
                      <div className="app-nav__delete-confirm" role="alert">
                        <p className="app-nav__delete-confirm-title">{t({ id: 'settings.deleteAccount.confirmTitle' })}</p>
                        <p className="app-nav__delete-confirm-text">{t({ id: 'settings.deleteAccount.confirmBody' })}</p>
                        {deleteError && (
                          <p className="app-nav__delete-confirm-error">{t({ id: 'settings.deleteAccount.error' })}</p>
                        )}
                        <div className="app-nav__delete-confirm-actions">
                          <button
                            type="button"
                            className="app-nav__delete-confirm-cancel"
                            disabled={deleting}
                            onClick={() => setConfirmingDelete(false)}
                          >
                            {t({ id: 'common.cancel' })}
                          </button>
                          <button
                            type="button"
                            className="app-nav__delete-confirm-action"
                            disabled={deleting}
                            onClick={() => void handleDeleteAccount()}
                          >
                            {deleting ? <Spinner size="sm" /> : t({ id: 'settings.deleteAccount.confirmAction' })}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => { setDeleteError(false); setConfirmingDelete(true); }}
                        className="app-nav__settings-logout app-nav__settings-delete"
                      >
                        <Trash2 size={16} />
                        <span>{t({ id: 'settings.deleteAccount' })}</span>
                      </button>
                    )}

                    <p className="app-nav__settings-legal">
                      <a href="/privacy" target="_blank" rel="noreferrer">{t({ id: 'legal.privacy' })}</a>
                      <span aria-hidden="true"> · </span>
                      <a href="/terms" target="_blank" rel="noreferrer">{t({ id: 'legal.terms' })}</a>
                    </p>
                  </div>
                </div>
          ) : null}
        />

        <main className="app-main">
          <Routes>
            <Route path="/" element={<DailyTracker />} />
            <Route path="/statistics" element={<YearlyTracker />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

          {onboardingActive && (
            <Onboarding
              locale={locale}
              onLocaleChange={onLocaleChange}
              completeOnboarding={completeOnboarding}
              setLocale={persistLocalePreference}
            />
          )}

          {layoutOpen && (
            <DailyLayoutSettings
              sections={settings.dailyLayout?.sections ?? DEFAULT_DAILY_SECTIONS}
              hiddenSections={settings.dailyLayout?.hiddenSections ?? DEFAULT_HIDDEN_SECTIONS}
              onSave={(nextSections, hidden) => {
                void updateSettings({ dailyLayout: { sections: nextSections, hiddenSections: hidden } });
              }}
              onClose={() => setLayoutOpen(false)}
            />
          )}
        </div>
        </HabitProvider>
        </SelectedDateProvider>
  );
};

export default AppShell;